import { useQuery } from '@tanstack/react-query';
import {
  collection,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import { COLLECTIONS } from '../../config/collections';
import { useAuth } from '../../contexts/AuthContext';
import { useOrganization } from '../../contexts/OrganizationContext';
import type { User } from '../../types/firestore';

/**
 * Twin of canReadQuoteDocs() in firestore.rules: an active, non-External user who is
 * Owner/Admin/Manager or holds the Quoting (P2) or Field Service (P11) module, never an
 * Investor. The count query is denied for anyone else, so it is not sent for them.
 */
function canReadQuotes(user: User | null): boolean {
  if (!user || user.isActive !== true || user.isExternal === true || user.role === 'Investor') return false;
  if (user.role === 'Owner' || user.role === 'Admin' || user.role === 'Manager') return true;
  const modules = user.enabledModules ?? [];
  return modules.includes('P2') || modules.includes('P11');
}

/** Count of quotes with unread @mentions for the current user */
export function useQuoteMentionCount() {
  const { firebaseUser, user } = useAuth();
  const { currentOrganization } = useOrganization();
  const userId = firebaseUser?.uid;
  const orgId = currentOrganization?.organizationId;

  return useQuery({
    queryKey: ['quotes', 'mention-count', userId, orgId],
    queryFn: async (): Promise<number> => {
      if (!userId || !orgId) return 0;

      const q = query(
        collection(db, COLLECTIONS.QUOTES),
        where('organizationId', '==', orgId),
        where('unreadMentionUserIds', 'array-contains', userId)
      );
      const snap = await getDocs(q);
      return snap.size;
    },
    staleTime: 60_000,
    enabled: !!userId && !!orgId && canReadQuotes(user),
  });
}
